import { useState } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getCardClass, isCardTransparent, isColorDark, getAnimation, hexToRgba, resolveLinkProps } from '../utils';
import { getButtonIcon, resolveIconKey } from '../buttonIcons';
import SectionHeader from './SectionHeader';
import SectionDivider from './SectionDivider';

export default function FaqContactSection({ block = {}, primaryColor, secondaryColor, enableAnimations, cardStyle, mediaFolders = {} }) {
    const { config = {}, content = {} } = block;
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const tp = isCardTransparent(cardStyle, s.dark);
    const variant = config.variant || 'split';

    const faqItems = content.faq_items || [];
    const [openIndex, setOpenIndex] = useState(null);

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };
    const itemBg = s.dark ? 'rgba(255,255,255,0.05)' : '#ffffff';
    const itemBorder = s.dark ? 'rgba(255,255,255,0.1)' : '#e5e7eb';
    const itemBorderOpen = s.dark ? 'rgba(255,255,255,0.2)' : '#d1d5db';

    const contactType = content.contact_type || 'whatsapp';
    const contactValue = content.contact_value || '';
    const whatsappMessage = content.whatsapp_message || '';

    const getContactHref = () => {
        if (contactType === 'whatsapp') {
            const num = contactValue.replace(/[^0-9]/g, '');
            const msg = whatsappMessage ? `?text=${encodeURIComponent(whatsappMessage)}` : '';
            return `https://wa.me/${num}${msg}`;
        }
        if (contactType === 'phone') return `tel:${contactValue}`;
        if (contactType === 'email') return `mailto:${contactValue}`;
        return '#';
    };

    const getContactIcon = () => {
        if (contactType === 'whatsapp') return (
            <svg className="w-8 h-8" fill="currentColor" viewBox="0 0 24 24">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
            </svg>
        );
        if (contactType === 'phone') return (
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
        );
        return (
            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        );
    };

    const getContactButtonText = () => {
        if (contactType === 'whatsapp') return 'Escribenos por WhatsApp';
        if (contactType === 'phone') return 'Llamanos';
        return 'Envianos un email';
    };

    const getContactButtonColor = () => {
        if (contactType === 'whatsapp') return '#25D366';
        if (contactType === 'phone') return '#3B82F6';
        return s.accent;
    };

    const AccordionItem = ({ item, i }) => {
        const isOpen = openIndex === i;
        return (
            <div className="rounded-xl border transition-all duration-300"
                style={{ backgroundColor: itemBg, borderColor: isOpen ? itemBorderOpen : itemBorder }}>
                <button onClick={() => setOpenIndex(isOpen ? null : i)} className="w-full flex items-center justify-between px-5 py-4 text-left">
                    <span className="text-sm font-semibold pr-4" style={{ color: s.text }}>{item.question}</span>
                    <svg className={`w-5 h-5 shrink-0 transition-transform duration-300 ${isOpen ? 'rotate-180' : ''}`}
                        style={{ color: s.accent }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </button>
                <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
                    <div className="px-5 pb-4 text-sm leading-relaxed whitespace-pre-line" style={{ color: s.textSub }}>{item.answer}</div>
                </div>
            </div>
        );
    };

    const FaqBlock = () => (
        <div className={`space-y-3 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
            {faqItems.map((item, i) => <AccordionItem key={i} item={item} i={i} />)}
        </div>
    );

    const ContactCard = () => {
        const btnColor = getContactButtonColor();
        return (
            <div className={`rounded-2xl p-6 md:p-8 transition-all duration-700 delay-200 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}
                style={{ backgroundColor: hexToRgba(s.accent, s.dark ? 0.15 : 0.06), borderRadius: '1rem' }}>
                <div className="text-center">
                    <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center"
                        style={{ backgroundColor: hexToRgba(s.accent, 0.15), color: s.accent }}>
                        {getContactIcon()}
                    </div>
                    {content.contact_title && (
                        <h3 className="text-lg font-bold mb-2" style={{ color: s.text }}>{content.contact_title}</h3>
                    )}
                    {content.contact_subtitle && (
                        <p className="text-sm leading-relaxed mb-4" style={{ color: s.textSub }}>{content.contact_subtitle}</p>
                    )}
                    <p className="text-sm font-medium mb-2" style={{ color: s.textSub }}>
                        No encontraste tu respuesta?
                    </p>
                    {contactValue && (
                        <p className="text-sm font-semibold mb-5" style={{ color: s.text }}>{contactValue}</p>
                    )}
                    <a
                        href={getContactHref()}
                        target={contactType === 'whatsapp' ? '_blank' : undefined}
                        rel={contactType === 'whatsapp' ? 'noopener noreferrer' : undefined}
                        className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm text-white transition-all hover:scale-105 hover:shadow-lg w-full justify-center"
                        style={{ backgroundColor: btnColor }}
                    >
                        {contactType === 'whatsapp' && (
                            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                            </svg>
                        )}
                        {contactType === 'phone' && (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                            </svg>
                        )}
                        {contactType === 'email' && (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                            </svg>
                        )}
                        {getContactButtonText()}
                    </a>
                </div>
            </div>
        );
    };

    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 ${anim}`}>
                <SectionHeader tag={content.tag} title={content.title} subtitle={content.subtitle} config={config} s={s} />

                {variant === 'stacked' ? (
                    <div className="max-w-3xl mx-auto space-y-10">
                        <FaqBlock />
                        <ContactCard />
                    </div>
                ) : (
                    /* split (default) */
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12 items-start">
                        <div className="lg:col-span-3">
                            <FaqBlock />
                        </div>
                        <div className="lg:col-span-2 lg:sticky lg:top-24">
                            <ContactCard />
                        </div>
                    </div>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
